/**
 * Generic bulk operations for entity lists (REACT-010)
 *
 * Provides shared utilities for archive, unarchive, change owner,
 * and label operations across packets, checklists, and contacts.
 */

import { apiClient } from './client';

export type EntityType = 'packet' | 'checklist' | 'contact';
export type BulkAction =
  | 'archive'
  | 'unarchive'
  | 'change_owner'
  | 'add_labels'
  | 'remove_labels';

export interface BulkSelectionParams {
  selectedIds: number[];
  excludedIds: number[];
  selectedAll: boolean;
}

/**
 * Filter out null/undefined ids from a selection array.
 *
 * Bug-hunt #17: callers assemble `selectedIds` / `excludedIds` by reducing
 * over state that can contain gaps (an entity deleted mid-flight, a
 * selection persisted across a refetch). `[1, undefined, 3].join(',')`
 * silently produces the string `"1,,3"` — which the backend parses as a
 * leading-zero or empty id. Filtering at the boundary keeps the wire format
 * clean and TypeScript-safe.
 */
function sanitizeIds(ids: ReadonlyArray<number | null | undefined>): number[] {
  return ids.filter((id): id is number => typeof id === 'number' && Number.isFinite(id));
}

/**
 * Build query params for bulk operations with selection
 */
export function buildBulkParams(
  selection: BulkSelectionParams,
  action: BulkAction,
  additionalParams?: Record<string, unknown>
): Record<string, unknown> {
  const params: Record<string, unknown> = { action, ...additionalParams };

  if (selection.selectedAll) {
    params.selected_all = true;
    const excluded = sanitizeIds(selection.excludedIds);
    if (excluded.length > 0) {
      params.excluded_ids = excluded.join(',');
    }
  } else {
    params.selected_ids = sanitizeIds(selection.selectedIds).join(',');
  }

  return params;
}

/**
 * Execute a bulk action on an entity endpoint.
 *
 * Bug-hunt #18: previously passed `null` as the body. FastAPI route decorators
 * vary in whether they accept that; `undefined` is the unambiguous "no body"
 * signal in axios.
 */
async function bulkAction(
  endpoint: string,
  selection: BulkSelectionParams,
  action: BulkAction,
  additionalParams?: Record<string, unknown>
): Promise<void> {
  const params = buildBulkParams(selection, action, additionalParams);
  await apiClient.put(endpoint, undefined, { params });
}

// Convenience wrappers for common operations

export const bulkArchive = (
  endpoint: string,
  selection: BulkSelectionParams
): Promise<void> => bulkAction(endpoint, selection, 'archive');

export const bulkUnarchive = (
  endpoint: string,
  selection: BulkSelectionParams
): Promise<void> => bulkAction(endpoint, selection, 'unarchive');

export const bulkChangeOwner = (
  endpoint: string,
  selection: BulkSelectionParams,
  ownerId: number
): Promise<void> =>
  bulkAction(endpoint, selection, 'change_owner', { owner_id: ownerId });

export const bulkAddLabels = (
  endpoint: string,
  selection: BulkSelectionParams,
  labels: string[]
): Promise<void> =>
  bulkAction(endpoint, selection, 'add_labels', { labels: labels.join(',') });

export const bulkRemoveLabels = (
  endpoint: string,
  selection: BulkSelectionParams,
  labels: string[]
): Promise<void> =>
  bulkAction(endpoint, selection, 'remove_labels', {
    labels: labels.join(','),
  });

// ============================================
// List archive / unarchive: POST /api/<entity>/bulk/ (vingapi#2146 S2)
// ============================================

export type ArchiveBulkAction = 'archive' | 'unarchive';

/** `selection` of POST /api/{packets,checklists,contacts}/bulk/ (BulkSelection). */
export interface ArchiveBulkSelectionBody {
  selected_ids: number[];
  excluded_ids: number[];
  request_url: string;
  count: number;
}

export interface ArchiveBulkBody {
  selection: ArchiveBulkSelectionBody;
  action: ArchiveBulkAction;
}

/**
 * 200 body (camelCased by the client). `leftOut`: selected items this actor may
 * not change that are NOT already in the target state; null for other actions.
 * Contacts also carry the #807 refusal list, which the list path no longer reads.
 */
export interface ArchiveBulkResult {
  affected: number;
  changed: number | null;
  leftOut: number | null;
}

/**
 * The one body builder for list archive / unarchive.
 * The server's BulkSelection requires `request_url` and `count` in BOTH modes
 * (vingapi src/core/bulk/schemas.py). Explicit selection -> `selected_ids`,
 * `excluded_ids: []`, `count` = the number of distinct ids (the resolver's
 * validate_count compares it with len(selected_ids - excluded_ids), resolver.py
 * `resolve`); select-all -> no ids, `excluded_ids`, `count` = `selectionCount`.
 * `request_url` is the list URL, paging stripped, in both.
 */
export function buildArchiveBulkBody(
  selection: BulkSelectionParams,
  listUrl: string,
  selectionCount: number,
  action: ArchiveBulkAction,
): ArchiveBulkBody {
  if (selection.selectedAll) {
    return {
      selection: {
        selected_ids: [],
        excluded_ids: sanitizeIds(selection.excludedIds),
        request_url: listUrl,
        count: selectionCount,
      },
      action,
    };
  }
  const selectedIds = [...new Set(sanitizeIds(selection.selectedIds))];
  return {
    selection: {
      selected_ids: selectedIds,
      excluded_ids: [],
      request_url: listUrl,
      count: selectedIds.length,
    },
    action,
  };
}

/** POST /api/<entity>s/bulk/ with an archive / unarchive body. */
export async function postArchiveBulk(
  entityType: EntityType,
  body: ArchiveBulkBody,
): Promise<ArchiveBulkResult> {
  const res = await apiClient.post<ArchiveBulkResult>(`/api/${entityType}s/bulk/`, body);
  return res.data;
}
